import { bootTheme, paintSavedTheme } from "./theme";
import { bootEvents, call } from "./bridge";
import { restoreViewFromHash, setupNavigation } from "./navigation";
import { setupAvatars } from "./avatar";
import { stampIcons } from "./icons";
import { paintLook } from "./look";
import { setupFx } from "./fx";
import { setupModalA11y } from "./focus_trap";
import { setupAriaSync } from "./aria_sync";
import { setupRefsStore } from "./refs_store";
import { setupPressFeel } from "./tweens";
import { setupJuice } from "./juice";
import { setupSignal } from "./signal";
import { setupTilt } from "./tilt";
import { setupCountUp } from "./countup";
import { setupSwipeNav } from "./swipe_nav";
import { setupTaskEditor } from "./views/task_editor";
import { setupTodayView } from "./views/today_view";
import { setupTasksView } from "./views/tasks_view";
import { setupNotesView } from "./views/notes_view";
import { setupReferencesView } from "./views/references_view";
import { setupChatView } from "./views/chat_view";
import { setupRemindersView } from "./views/reminders_view";
import { setupRoutinesView } from "./views/routines_view";
import { setupStudioView } from "./views/studio_view";
import { setupBudgetView } from "./views/budget_view";
import { setupMapsView } from "./views/maps_view";
import { setupGoalsView } from "./views/goals_view";
import { setupRouletteView } from "./views/roulette_view";
import { setupCatView } from "./views/cat_view";
import { setupGestureView } from "./views/gesture_view";
import { setupJapaneseView } from "./views/japanese_view";
import { setupOcsView } from "./views/ocs_view";
import { setupClaudeView } from "./views/claude_view";
import { setupWebView } from "./views/web_view";
import { setupSettingsView } from "./views/settings_view";
import { setupLogsView } from "./views/logs_view";
import { setupServerView } from "./views/server_view";
import { setupCalendarView } from "./views/calendar_view";
import { setupCalculatorView } from "./views/calculator_view";
import { setupScanView } from "./views/scan_view";
import { setupDoodlesView } from "./views/doodles_view";
import { setupWhiteboardView } from "./views/whiteboard_view";
import { setupTierlistsView } from "./views/tierlists_view";
import { setupTrackerView } from "./views/tracker_view";
import { setupMusicView } from "./views/music_view";
import { setupPeopleView } from "./views/people_view";
import { setupProfileView } from "./views/profile_view";
import { setupSharingView } from "./views/sharing_view";
import { setupAchievementsView } from "./views/achievements_view";
import { setupFeedView } from "./views/feed_view";
import { setupAwardsStore } from "./awards_store";
import { watchShares } from "./shares";
import { canUse, myAccess, setMe } from "./me";
import { limitPlaces, localizePlaces } from "./nav_map";
import { currentLanguage, setLanguage, translatePage } from "./i18n";
import { CHANNELS } from "../shared/channels";
import { cachedSettings } from "./settings_cache";

// views only wire their buttons at boot, each one fetches its data the first time it is shown

// bits of shared pages (the today desk) that belong to a feature this person doesn't have
function hideUnshared(): void {
  limitPlaces(myAccess());
  document.querySelectorAll<HTMLElement>("[data-feature]").forEach((element) => {
    element.classList.toggle("feature-off", !canUse(element.dataset.feature || ""));
  });
}

// views that only wire buttons and a "shown" hook: they run when the browser is idle, or at once on the first input
// or when the page opens straight onto one of them (so no show hook is registered too late)
const lazySetups: Array<() => void> = [
  setupGestureView, setupJapaneseView, setupCalendarView, setupCalculatorView, setupServerView, setupScanView,
  setupWhiteboardView, setupTierlistsView, setupTrackerView, setupPeopleView, setupSharingView, setupBudgetView,
  setupMapsView, setupGoalsView, setupRouletteView, setupCatView
];
let lazyDone = false;

function flushLazy(): void {
  if (lazyDone) return;
  lazyDone = true;
  for (const run of lazySetups) {
    try {
      run();
    } catch (error) {
      console.error(error);
    }
  }
}

function deferLazy(): void {
  const input = { capture: true, once: true, passive: true } as const;
  for (const name of ["pointerdown", "keydown", "touchstart"]) window.addEventListener(name, flushLazy, input);
  const idle = window.requestIdleCallback as ((run: () => void, options?: { timeout: number }) => number) | undefined;
  if (idle) idle(flushLazy, { timeout: 2500 });
  else window.setTimeout(flushLazy, 1200);
}

function startsOnToday(): boolean {
  const hash = location.hash.replace(/^#/, "").split("/")[0];
  if (hash) return hash === "today";
  try {
    const saved = JSON.parse(localStorage.getItem("chix-view") || "null") as { view?: string } | null;
    return !saved?.view || saved.view === "today";
  } catch {
    return true;
  }
}

async function bootViews(): Promise<void> {
  setupRefsStore();
  setupNavigation();
  setupTaskEditor();
  setupChatView();
  setupRemindersView();
  setupRoutinesView();
  setupDoodlesView();
  setupMusicView();
  setupProfileView();
  setupAwardsStore();
  setupAchievementsView();
  setupFeedView();
  watchShares();
  await Promise.all([
    setupTodayView(),
    setupTasksView(),
    setupNotesView(),
    setupReferencesView(),
    setupWebView(),
    setupSettingsView(),
    setupLogsView(),
    setupStudioView(),
    setupOcsView(),
    setupClaudeView()
  ]);
}

async function webLogin(): Promise<boolean> {
  const gate = document.getElementById("login-gate");
  const me = await fetch("/auth/me", { credentials: "include" }).then((response) => response.ok ? response.json() : null).catch(() => null);
  if (me) {
    setMe(me);
    if (gate) gate.hidden = true;
    return true;
  }
  if (gate) gate.hidden = false;
  document.getElementById("login-discord")?.addEventListener("click", () => {
    location.href = "/auth/discord";
  });
  document.getElementById("login-setup")?.addEventListener("submit", async (event) => {
    event.preventDefault();
    const token = (document.getElementById("login-setup-token") as HTMLInputElement)?.value || "";
    const response = await fetch("/auth/setup", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token })
    });
    if (response.ok) location.reload();
    else (document.getElementById("login-status") as HTMLElement).textContent = "that setup token was refused";
  });
  return false;
}

// this device remembers the last language so the login card already speaks it, the person's own setting wins once it loads
async function bootLanguage(): Promise<void> {
  const settings = await cachedSettings().catch(() => null);
  if (settings && setLanguage(settings.language)) translatePage();
  localizePlaces();
  if (window.bridge) void call(CHANNELS.appLanguage, currentLanguage()).catch(() => undefined);
}

async function boot(): Promise<void> {
  paintSavedTheme();
  paintLook();
  setupFx();
  setupModalA11y();
  setupAriaSync();
  translatePage();
  stampIcons();
  setupPressFeel();
  setupJuice();
  setupTilt();
  setupSignal();
  setupCountUp();
  setupSwipeNav();
  setupAvatars();
  if (!window.bridge) {
    document.body.classList.add("is-web");
    if (!(await webLogin())) return;
  }
  window.setTimeout(() => document.body.classList.remove("booting"), 900);
  void bootEvents();
  void bootTheme();
  await bootLanguage();
  hideUnshared();
  await bootViews();
  if (startsOnToday()) deferLazy();
  else flushLazy();
  restoreViewFromHash();
}

void boot();
